import React, { useEffect, useRef, useContext, Fragment } from "react";
import { Formik, Form } from "formik";
import TextInput from "components/FormElements/TextInput";
import FastTextInput from "components/FormElements/FastTextInput";
import Label from "components/FormElements/Label";
import { useAuth } from "hooks/Auth";
import WorkflowField from "Screens/Container/Fields/WorkflowField";
import TagField from "Screens/Container/Fields/TagField";
import LayoutTemplateField from "Screens/Container/Fields/LayoutTemplateField";
import UuidField from "Screens/Container/Fields/UuidField";
import ContainerMeta from "Screens/Container/Fields/ContainerMeta";
import ContentList from "Screens/Container/Fields/ContentList";
import ContainerToolbar from "Screens/Container/ContainerToolbar";
import StaticDateField from "Screens/Container/Fields/StaticDateField";
import { ContainerContext } from "context/Container";
import PublishButton from "Screens/Container/Fields/PublishButton";
import RichEditor from "components/FormElements/RichEditor";
import ContainerTypeField from "./Fields/ContainerTypeField";
import PromptIfDirty from "components/FormElements/PromptIfDirty";
import CheckBox from "components/FormElements/CheckBox";

function EditForm(props) {
    const auth = useAuth();
    const formRef = useRef();
    const { state: containerState } = useContext(ContainerContext);

    // simply watches the loading indicator and resets the form when it changes.
    // this should always be the last `useEffect` in the sequence.
    useEffect(() => {
        setTimeout(() => {
            formRef.current.resetForm({
                values: containerState.formValues,
            });
        });
    }, [containerState.loading, containerState.formValues]);

    return (
        <Formik
            innerRef={formRef}
            initialValues={containerState.formValues}
            onSubmit={props.onSubmit}
            enableReinitialize={false}
        >
            {({ setFieldValue }) => (
                <Form>
                    <PromptIfDirty />
                    <div className="relative">
                        <div className="sticky top-0 z-10">
                            <ContainerToolbar />
                        </div>
                        <div className="grid grid-cols-12 gap-4 px-2 sm:px-0">
                            <div className="order-last col-span-12 space-y-4 md:col-span-3">
                                <UuidField />
                                <div className="space-y-4 rounded bg-white p-4 shadow-sm">
                                    <StaticDateField
                                        label="Created At:"
                                        field="created_at"
                                        defaultValue="Unsaved"
                                    />
                                    <StaticDateField
                                        label="Modified At:"
                                        field="modified_at"
                                        defaultValue="Unsaved"
                                    />
                                    <StaticDateField
                                        label="Published At:"
                                        field="published_at"
                                        defaultValue="Draft"
                                    />
                                    <hr />
                                    <div className="mx-0 flex items-end justify-evenly space-x-4 px-0">
                                        <div className="flex-grow">
                                            <PublishButton />
                                        </div>
                                    </div>
                                </div>

                                <div className="rounded bg-white p-4 shadow-sm">
                                    <ContainerTypeField />
                                </div>

                                <div className="rounded bg-white p-4 shadow-sm">
                                    <WorkflowField />
                                </div>

                                <div className="rounded bg-white p-4 shadow-sm">
                                    <TagField />
                                    <hr className="my-2" />
                                    <div className="flex items-center">
                                        <CheckBox name="tag_all" />
                                        <Label>
                                            Content must have all tags
                                        </Label>
                                    </div>
                                </div>

                                <div className="rounded bg-white p-4 shadow-sm">
                                    <LayoutTemplateField />
                                </div>
                            </div>
                            <div className="order-first col-span-12 space-y-4 md:col-span-9">
                                {containerState.container.uuid && (
                                    <div>
                                        <Label>URLs</Label>
                                        {containerState.loading ? (
                                            <div className="mt-1 h-20 w-full animate-pulse rounded bg-gray-300 text-gray-300"></div>
                                        ) : (
                                            <div className="space-y-2 text-xs">
                                                <div>
                                                    <a
                                                        href={`${auth.publication.uri}/${containerState.container.uuid}`}
                                                        className="text-green-600 underline"
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        {auth.publication.uri}/
                                                        {
                                                            containerState
                                                                .container.uuid
                                                        }
                                                    </a>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {containerState.loading ? (
                                    <Fragment>
                                        <div>
                                            <Label>Title</Label>
                                            <div className="mt-1 h-9 w-full animate-pulse rounded bg-gray-300 text-gray-300"></div>
                                        </div>
                                        <div>
                                            <Label>URL Slug</Label>
                                            <div className="mt-1 h-9 w-full animate-pulse rounded bg-gray-300 text-gray-300"></div>
                                        </div>
                                    </Fragment>
                                ) : (
                                    <Fragment>
                                        <FastTextInput
                                            label="Title"
                                            name="title"
                                        />

                                        <TextInput
                                            label="URL Slug"
                                            name="slug"
                                            disabled={
                                                containerState.loading ||
                                                containerState.preformingStateChange ||
                                                !containerState.isUserEditable
                                            }
                                            onChange={(e) => {
                                                const newVal = e.target.value
                                                    .toLowerCase()
                                                    .replace(/ {2,}/g, " ")
                                                    .replace(/ /g, "-")
                                                    .replace(/[^a-z0-9-]/g, "");

                                                setFieldValue("slug", newVal);
                                            }}
                                            helpertext="URL slugs are used to generate the URL for the article, they should be short but descriptive."
                                        />

                                        <ContentList />
                                    </Fragment>
                                )}
                                <div className="relative z-20 mt-4">
                                    {containerState.loading ? (
                                        <div className="absolute top-0 left-0 z-50 h-full w-full bg-gray-50">
                                            <div className="h-full w-full animate-pulse rounded bg-gray-300"></div>
                                        </div>
                                    ) : null}
                                </div>
                                <div className="space-y-4">
                                    <div>
                                        <Label>Description</Label>
                                        <RichEditor name="description" />
                                    </div>
                                    <div>
                                        <ContainerMeta block="container" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Form>
            )}
        </Formik>
    );
}

export default EditForm;
